import React from "react";
import {
  CalendarCheck2, CalendarDays, CalendarX2, Clock, DoorOpen, ExternalLink, KeyRound,
  Monitor, Phone, ShieldOff, Video,
} from "lucide-react";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle, UserAvatar } from "../../ui";
import { formatDate, formatTimeOfDay, DASH } from "../../../utils/format";
import { batchSchedule } from "./batchDetails";

const MEETING_ID_LABEL = {
  zoom: "Zoom meeting ID",
  zoho: "Zoho meeting ID",
  grats: "Grats Meet room",
};

/**
 * One labelled fact. A `<div>` holding a `<dt>`/`<dd>` pair, so it must sit in
 * a `<dl>`: the label is the term, the value its description.
 */
const Fact = ({ icon: Icon, label, children }) => (
  <div className="tw-min-w-0">
    <dt className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-content-subtle">
      <Icon size={14} className="tw-shrink-0" aria-hidden="true" />
      {label}
    </dt>
    <dd className="tw-m-0 tw-mt-1 tw-break-words tw-text-sm tw-font-medium tw-text-content">
      {children ?? DASH}
    </dd>
  </div>
);

const classTime = (batch) => {
  const start = formatTimeOfDay(batch.startTime);
  const end = formatTimeOfDay(batch.endTime);
  if (start === DASH) return null;
  return end === DASH ? start : `${start} – ${end}`;
};

/**
 * BatchFactsCard — when and where the batch meets: dates, days, time, mode,
 * room, the meeting it joins, and the content expiry date.
 */
export const BatchFactsCard = ({ batch }) => {
  const days = batchSchedule({ meetingDays: batch.meetingDays, batchType: batch.batchType });

  return (
    <AppCard as="section" aria-labelledby="batch-facts-title">
      <AppCardHeader>
        <AppCardTitle id="batch-facts-title">Schedule and logistics</AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4 min-[768px]:tw-grid-cols-3 min-[1200px]:tw-grid-cols-4">
          <Fact icon={CalendarCheck2} label="Start date">{batch.startDate ? formatDate(batch.startDate) : null}</Fact>
          <Fact icon={CalendarX2} label="End date">{batch.endDate ? formatDate(batch.endDate) : null}</Fact>
          <Fact icon={CalendarDays} label="Class days">{days || null}</Fact>
          <Fact icon={Clock} label="Class time">{classTime(batch)}</Fact>
          <Fact icon={Monitor} label="Mode">{batch.mode || null}</Fact>
          <Fact icon={DoorOpen} label="Room">{batch.room || null}</Fact>
          {batch.expiryDate && (
            <Fact icon={ShieldOff} label="Content expiry">{formatDate(batch.expiryDate)}</Fact>
          )}
          {batch.meetingLink && (
            <Fact icon={ExternalLink} label="Meeting link">
              <a
                href={batch.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-sm tw-text-primary tw-no-underline tw-focus-ring hover:tw-underline"
              >
                Open link <ExternalLink size={12} aria-hidden="true" />
                <span className="tw-sr-only"> (opens in a new tab)</span>
              </a>
            </Fact>
          )}
          {batch.zoomMeetingId && (
            <Fact icon={Video} label={MEETING_ID_LABEL[batch.meetingProvider] || MEETING_ID_LABEL.zoom}>
              <span className="tw-font-mono tw-text-[0.8125rem]">{batch.zoomMeetingId}</span>
            </Fact>
          )}
          {batch.zoomPasscode && (
            <Fact icon={KeyRound} label="Passcode">
              <span className="tw-font-mono tw-text-[0.8125rem]">{batch.zoomPasscode}</span>
            </Fact>
          )}
        </dl>
      </AppCardContent>
    </AppCard>
  );
};

/**
 * BatchTeamCard — who teaches the batch, and how to reach them.
 *
 * Props:
 *   instructors — batchDetails.batchInstructors()
 */
export const BatchTeamCard = ({ instructors = [] }) => (
  <AppCard as="section" aria-labelledby="batch-team-title">
    <AppCardHeader>
      <AppCardTitle id="batch-team-title">
        {instructors.length > 1 ? "Instructors" : "Instructor"}
      </AppCardTitle>
    </AppCardHeader>
    <AppCardContent>
      {instructors.length === 0 ? (
        <p className="tw-m-0 tw-text-sm tw-text-content-subtle">No instructor assigned yet.</p>
      ) : (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
          {instructors.map((instructor) => (
            <li key={instructor._id || instructor.name} className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
              <UserAvatar name={instructor.name || "?"} size="sm" />
              <div className="tw-min-w-0">
                <p className="tw-m-0 tw-truncate tw-text-sm tw-font-medium tw-text-content">{instructor.name || "Unnamed"}</p>
                {instructor.mobile && (
                  <a
                    href={`tel:${instructor.mobile}`}
                    aria-label={`Call ${instructor.name || "instructor"} on ${instructor.mobile}`}
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-sm tw-text-xs tw-text-content-muted tw-no-underline tw-focus-ring hover:tw-text-primary"
                  >
                    <Phone size={12} aria-hidden="true" /> {instructor.mobile}
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </AppCardContent>
  </AppCard>
);
